import React, {useState} from "react";
import {CONTACT_CARDS, CONTACT_TOPICS, PHOTO} from "../../data/siteData";
import {sendWebsiteContact} from "../../services/CommonService";
import Utils, {onLight, stagger, TINT} from "../../utils";
import ContactText from "../common/ContactText";
import Icon from "../common/Icon";
import ImageSlot from "../common/ImageSlot";
import SectionIntro from "../common/SectionIntro";

const EMPTY_FORM = {name: '', email: '', topic: CONTACT_TOPICS[0], message: '', website: ''};

// Same rules as the API (website-contact), so most mistakes are caught before sending.
const validate = (form) => {
    const errors = {};
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (name.length < 2 || name.length > 100) errors.name = 'Please enter your name (2 to 100 characters).';
    if (email.length > 191 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) errors.email = 'Please enter a valid email address.';
    if (!CONTACT_TOPICS.includes(form.topic)) errors.topic = 'Please choose a topic from the list.';
    if (message.length < 10 || message.length > 2000) errors.message = 'Please write a message of 10 to 2000 characters.';
    return errors;
};

const ContactForm = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);
    const [result, setResult] = useState(null);

    const onChange = (e) => {
        const {name, value} = e.target;
        setForm(f => ({...f, [name]: value}));
        if (errors[name]) setErrors(({[name]: _, ...rest}) => rest);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const found = validate(form);
        setErrors(found);
        setResult(null);
        if (Object.keys(found).length) return;

        setSending(true);
        sendWebsiteContact({
            name: form.name.trim(), email: form.email.trim(), topic: form.topic,
            message: form.message.trim(), website: form.website
        })
            .then(res => {
                setForm(EMPTY_FORM);
                setResult({ok: true, text: res.data.message});
            })
            .catch(err => {
                const data = err.response && err.response.data;
                if (data && data.errors) setErrors(data.errors);
                setResult({ok: false, text: Utils.getErrorMessage(err)});
            })
            .finally(() => setSending(false));
    };

    const field = (name) => ({
        name, value: form[name], onChange,
        'aria-invalid': errors[name] ? true : undefined,
        'aria-describedby': errors[name] ? `contact-${name}-error` : undefined
    });
    const error = (name) => errors[name] &&
        <small id={`contact-${name}-error`} className="pb-field-error">{errors[name]}</small>;

    return (
        <form className="pb-card pb-contact-form" onSubmit={onSubmit} noValidate>
            <label className="pb-field">
                <span>Name</span>
                <input {...field('name')} autoComplete="name" maxLength={100}/>
                {error('name')}
            </label>
            <label className="pb-field">
                <span>Email</span>
                <input type="email" {...field('email')} autoComplete="email" maxLength={191}/>
                {error('email')}
            </label>
            <label className="pb-field">
                <span>Topic</span>
                <select {...field('topic')}>
                    {CONTACT_TOPICS.map(topic => <option key={topic}>{topic}</option>)}
                </select>
                {error('topic')}
            </label>
            <label className="pb-field">
                <span>Message</span>
                <textarea rows={5} {...field('message')} maxLength={2000}/>
                {error('message')}
            </label>
            {/* Honeypot: hidden from people, bots fill it in and the API then drops the message. */}
            <input type="text" name="website" value={form.website} onChange={onChange}
                   tabIndex={-1} autoComplete="off" aria-hidden="true" className="pb-honeypot"/>
            {result && (
                <p role="status" className={result.ok ? 'pb-form-note pb-form-note-ok' : 'pb-form-note pb-form-note-error'}>
                    {result.text}
                </p>
            )}
            <button type="submit" className="pb-btn-send hover-brighten" disabled={sending}>
                <Icon name="mail" size={16}/>{sending ? 'Sending…' : 'Send message'}
            </button>
        </form>
    );
};

const ContactSection = () => (
    <>
        <SectionIntro
            id="contact"
            pill={{icon: 'mail', text: 'Contact', bg: 'rgba(150,41,142,0.13)', color: '#96298E'}}
            title={<>Talk to <span style={{color: '#96298E'}}>a human</span></>}
            titleStyle={{maxWidth: '16ch'}}
            lead="A booking you already have, a box that will not open, or hosting one at your own site."
        />
        <section className="pb-container pb-section-body">
            <div className="pb-contact-grid">
                <ContactForm/>
                <div style={{display: 'flex', flexDirection: 'column', gap: 18}}>
                    {CONTACT_CARDS.map((c, i) => (
                        <div key={c.title} className="pb-reveal pb-contact-card"
                             style={{borderLeftColor: c.color, animationDelay: stagger(i)}}>
                            <span className="pb-fact-icon" style={{background: TINT[c.color], color: onLight(c.color)}}>
                                <Icon name={c.icon} size={22}/>
                            </span>
                            <h3>{c.title}</h3>
                            <p><ContactText text={c.body}/></p>
                            {c.note && <p className="pb-contact-note">{c.note}</p>}
                        </div>
                    ))}
                    <div className="pb-contact-photo">
                        <ImageSlot src={PHOTO.contact} alt="Team servicing a box"/>
                    </div>
                </div>
            </div>
        </section>
    </>
);

export default ContactSection;
